/**
 * Platform detection utilities for determining the current meeting platform context
 *
 * These are PATH based ("/teams/..."): they say which platform's routes are on screen. Pages
 * outside the platform routes (the /503, /500 and 404 error pages) cannot use them; those use
 * detectEnvironmentPlatform() in features/config/AppEnvironmentDetector/utils, which looks at the
 * user agent and the embedding frame. The two disagree there by design.
 */

export type PlatformContext = 'zoom' | 'teams' | 'webex' | 'meet' | 'web';

/**
 * Detects if the app is currently running in Zoom context
 */
export const isZoomContext = (): boolean => {
  const pathname = window.location.pathname;
  return pathname.startsWith('/zoom');
};

/**
 * Detects if the app is currently running in Teams context
 */
export const isTeamsContext = (): boolean => {
  const pathname = window.location.pathname;
  return pathname.startsWith('/teams');
};

/**
 * Detects if the app is currently running in WebEx context
 */
export const isWebexContext = (): boolean => {
  const pathname = window.location.pathname;
  return pathname.startsWith('/webex');
};

/**
 * Detects if the app is currently running in Google Meet context
 */
export const isMeetContext = (): boolean => {
  const pathname = window.location.pathname;
  return pathname.startsWith('/meet');
};

/**
 * Gets the current platform context
 */
export const getCurrentPlatformContext = (): PlatformContext => {
  if (isZoomContext()) return 'zoom';
  if (isTeamsContext()) return 'teams';
  if (isWebexContext()) return 'webex';
  if (isMeetContext()) return 'meet';
  return 'web';
};

/**
 * Checks if the current platform supports consent requests
 * Currently Zoom and Teams support this functionality
 */
export const supportsConsentRequests = (): boolean => {
  return isZoomContext() || isTeamsContext();
};
